/**
 * Track B — CustomerSettingsPage (`/dashboard/customer/settings`).
 *
 * Dedicated home for the customer's timezone edit field. Loads via
 * `GET /profiles/customer/me`, saves via `PATCH /profiles/customer`.
 *
 * Pre-fill logic:
 *   - Backend returns non-empty `timezone` → use that as initial value.
 *   - Backend returns null/empty → pre-fill with browser-detected
 *     timezone AND surface the "not yet set — please confirm" hint
 *     so legacy-cohort users (37% of customers per Phase O
 *     telemetry) understand this is their chance to self-correct
 *     the ET-fallback.
 *
 * Save-success confirmation shown via `sonner` toast.
 */
import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fetchTimezones, detectBrowserTimezone } from "@/lib/timezones";
import { Loader2 } from "lucide-react";

export default function CustomerSettingsPage() {
  const { setProfile } = useAuth();
  const [tzList, setTzList] = useState([]);
  const [tz, setTz] = useState("");
  const [storedTz, setStoredTz] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNotSetHint, setShowNotSetHint] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [tzOptions, meResp] = await Promise.all([
          fetchTimezones(),
          api.get("/profiles/customer/me"),
        ]);
        if (!active) return;
        setTzList(tzOptions);
        const backendTz = meResp?.data?.timezone;
        setStoredTz(backendTz || null);
        if (backendTz) {
          setTz(backendTz);
          setShowNotSetHint(false);
        } else {
          setTz(detectBrowserTimezone());
          setShowNotSetHint(true);
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (active) setLoaded(true);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!tz) {
      toast.error("Please choose a timezone before saving.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/profiles/customer", { timezone: tz });
      setProfile(data);
      setStoredTz(data?.timezone || tz);
      setShowNotSetHint(false);
      toast.success("Timezone saved.");
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || e.message,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6" data-testid="customer-settings-page">
      <h1 className="text-2xl font-semibold mb-2 text-pp-slate">Settings</h1>
      <p className="text-sm text-pp-slate/70 mb-6">
        Personal preferences that apply across your PooPluckers account.
      </p>

      <Card className="p-6">
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <Label htmlFor="tz-select" className="block mb-2">
              Timezone
            </Label>
            {!loaded ? (
              <div className="flex items-center gap-2 text-sm text-pp-slate/70">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <>
                <select
                  id="tz-select"
                  data-testid="customer-settings-tz-select"
                  className="pp-select w-full rounded-md border px-3 py-2 text-sm"
                  value={tz}
                  onChange={(e) => setTz(e.target.value)}
                >
                  {!tz && <option value="">— pick a timezone —</option>}
                  {tzList.map((z) => (
                    <option key={z} value={z}>{z}</option>
                  ))}
                </select>
                {showNotSetHint && !storedTz ? (
                  <p
                    className="mt-2 text-xs text-pp-orange"
                    data-testid="customer-settings-tz-hint"
                  >
                    Not yet set — please confirm. We auto-detected your
                    browser's timezone; change it if needed.
                  </p>
                ) : (
                  <p className="mt-2 text-xs text-pp-slate/60">
                    Used to schedule your booking reminders at the right local time.
                  </p>
                )}
              </>
            )}
          </div>
          <div>
            <Button
              type="submit"
              disabled={saving || !tz}
              data-testid="customer-settings-save"
            >
              {saving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
              ) : "Save"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
